"use client";

import {
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonPage,
  IonTitle,
  IonToolbar,
  IonLoading,
} from "@ionic/react";
// import "./Page.css";
import type React from "react";
import { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Stack,
  Grid,
  Card,
  Typography,
  Box,
  Chip,
  CardContent,
  Divider,
  Container,
  Paper,
  IconButton,
  Fade,
  Zoom,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Work as WorkIcon,
  AttachMoney as MoneyIcon,
  Label as TagIcon,
  Folder as ProjectIcon,
  TrendingUp as TrendingIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import axios from "axios";
// import { baseUrl, config, requestID } from "../../../common/utils";
import { Link } from "react-router-dom";
import { requestID, baseUrl, config } from "../../../common/utils";
 
const ProjectSetup: React.FC = () => {
  // Project States
  const [projectName, setProjectName] = useState("");
  const [projectTags, setProjectTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [budget, setBudget] = useState("");
  const [projectData, setProjectData] = useState([]);
  const [status, setStatus] = useState({
    loading: false,
    error: false,
    message: "",
  });

  // New state for popup
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({
    projectName: "",
    budget: "",
    tags: "",
  });

  const addTag = () => {
    if (tagInput && !projectTags.includes(tagInput)) {
      setProjectTags([...projectTags, tagInput]);
      setTagInput("");
      setFormErrors({ ...formErrors, tags: "" });
    }
  };

  const removeTag = (tag: string) => {
    setProjectTags(projectTags.filter((t) => t !== tag));
  };

  // Form validation function
  const validateForm = () => {
    const errors = {
      projectName: "",
      budget: "",
      tags: "",
    };

    if (!projectName.trim()) {
      errors.projectName = "Project name is required";
    }

    if (!budget.trim()) {
      errors.budget = "Budget is required";
    } else if (isNaN(Number(budget)) || Number(budget) <= 0) {
      errors.budget = "Budget must be a valid positive number";
    }

    if (projectTags.length === 0) {
      errors.tags = "At least one tag is required";
    }

    setFormErrors(errors);
    return !errors.projectName && !errors.budget && !errors.tags;
  };

  const resetForm = () => {
    setProjectName("");
    setProjectTags([]);
    setTagInput("");
    setBudget("");
    setFormErrors({
      projectName: "",
      budget: "",
      tags: "",
    });
  };

  const handleOpenCreateProject = () => {
    setCreateProjectOpen(true);
  };

  const handleCloseCreateProject = () => {
    setCreateProjectOpen(false);
    resetForm();
  };

  async function createProject() {
    if (!validateForm()) {
      return;
    }

    setStatus({ loading: true, error: false, message: "Creating project..." });

    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("name", projectName);
    formData.append("tags", projectTags.toString());
    formData.append("budgetcosts", budget);

    try {
      const response = await axios.post(
        baseUrl() + "projects/addMyProject",
        formData,
        config
      );
      setStatus({
        loading: false,
        error: false,
        message: "Project created successfully!",
      });
      console.log(response);
      getAllProject();
      handleCloseCreateProject(); // Close popup after successful creation
    } catch (error) {
      console.error(error);
      setStatus({
        loading: false,
        error: true,
        message: "Failed to create project",
      });
    }
  }

  useEffect(() => {
    getAllProject();
  }, []);

  async function getAllProject() {
    setStatus({ loading: true, error: false, message: "Fetching projects..." });

    const formData = new FormData();
    formData.append("requestID", requestID());

    try {
      const response = await axios.post(
        baseUrl() + "projects/getAllProjects",
        formData,
        config
      );
      setProjectData(response.data.data);
      console.log(response.data);

      setStatus({ loading: false, error: false, message: "" });
    } catch (error) {
      console.error(error);
      setStatus({
        loading: false,
        error: true,
        message: "Failed to load projects",
      });
    }
  }

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle style={{ fontWeight: 600 }}>Manage Your Projects</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <Box
          sx={{
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
            minHeight: "100vh",
            py: 4,
          }}
        >
          <Container maxWidth="lg">
            {/* Loading Spinner */}
            <IonLoading
              isOpen={status.loading}
              message={status.message || "Please wait..."}
              spinner="crescent"
            />

            {/* Create Project Button - Replaces the form */}
            <Zoom in timeout={1000}>
              <Box textAlign="center" mb={2}>
                <Button
                  variant="contained"
                  onClick={handleOpenCreateProject}
                  size="large"
                  startIcon={<AddIcon />}
                  sx={{
                    py: 1.5,
                    px: 3,
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    fontSize: "1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: "0 4px 20px rgba(102, 126, 234, 0.4)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 25px rgba(102, 126, 234, 0.5)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Create New Project
                </Button>
              </Box>
            </Zoom>

            <Fade in timeout={800}>
              <Box textAlign="center" mb={2}>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  fontSize={"13px"}
                  maxWidth="600px"
                  mx="auto"
                >
                  Create, organize, and track your projects with our modern
                  project management system
                </Typography>
              </Box>
            </Fade>

            {/* Create Project Dialog/Popup */}
            <Dialog
              open={createProjectOpen}
              onClose={handleCloseCreateProject}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(10px)",
                },
              }}
            >
              <DialogTitle>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box display="flex" alignItems="center">
                    <WorkIcon sx={{ mr: 2, color: "#667eea", fontSize: 32 }} />
                    <Typography variant="h5" fontWeight="bold">
                      Create New Project
                    </Typography>
                  </Box>
                  <IconButton onClick={handleCloseCreateProject}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>

              <DialogContent>
                {status.message && (
                  <Alert
                    severity={status.error ? "error" : "success"}
                    sx={{ mb: 2 }}
                  >
                    {status.message}
                  </Alert>
                )}

                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Project Name"
                      variant="outlined"
                      value={projectName}
                      onChange={(e) => {
                        setProjectName(e.target.value);
                        if (e.target.value.trim()) {
                          setFormErrors({ ...formErrors, projectName: "" });
                        }
                      }}
                      error={!!formErrors.projectName}
                      helperText={formErrors.projectName}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <ProjectIcon sx={{ mr: 1, color: "#667eea" }} />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Budget (₦)"
                      type="number"
                      variant="outlined"
                      value={budget}
                      onChange={(e) => {
                        setBudget(e.target.value);
                        if (
                          e.target.value.trim() &&
                          !isNaN(Number(e.target.value)) &&
                          Number(e.target.value) > 0
                        ) {
                          setFormErrors({ ...formErrors, budget: "" });
                        }
                      }}
                      error={!!formErrors.budget}
                      helperText={formErrors.budget}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <span
                            style={{
                              marginRight: 8,
                              color: "#667eea",
                              fontWeight: 600,
                            }}
                          >
                            ₦
                          </span>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        mb={2}
                      >
                        <TextField
                          label="Add Role Tag"
                          variant="outlined"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          fullWidth
                          onKeyPress={(e) => e.key === "Enter" && addTag()}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#667eea",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#667eea",
                              },
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <TagIcon sx={{ mr: 1, color: "#667eea" }} />
                            ),
                          }}
                        />
                        <IconButton
                          onClick={addTag}
                          sx={{
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            color: "white",
                            width: 56,
                            height: 56,
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                              transform: "scale(1.05)",
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Stack>

                      {formErrors.tags && (
                        <Typography
                          color="error"
                          variant="caption"
                          sx={{ mb: 1, display: "block" }}
                        >
                          {formErrors.tags}
                        </Typography>
                      )}

                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {projectTags.map((tag, index) => (
                          <Zoom
                            in
                            key={tag}
                            timeout={300}
                            style={{ transitionDelay: `${index * 100}ms` }}
                          >
                            <Chip
                              label={tag}
                              onDelete={() => removeTag(tag)}
                              sx={{
                                background:
                                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: "white",
                                fontWeight: 500,
                                "& .MuiChip-deleteIcon": {
                                  color: "rgba(255, 255, 255, 0.8)",
                                  "&:hover": {
                                    color: "white",
                                  },
                                },
                              }}
                            />
                          </Zoom>
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions sx={{ p: 3, gap: 2 }}>
                <Button
                  onClick={handleCloseCreateProject}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={createProject}
                  size="large"
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    fontSize: "1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: "0 4px 20px rgba(102, 126, 234, 0.4)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 25px rgba(102, 126, 234, 0.5)",
                    },
                    "&:disabled": {
                      background: "#e0e0e0",
                      color: "#9e9e9e",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Create Project
                </Button>
              </DialogActions>
            </Dialog>

            <Fade in timeout={1200}>
              <Box>
                <Box display="flex" alignItems="center" mb={4}>
                  <TrendingIcon
                    sx={{ mr: 2, color: "#667eea", fontSize: 32 }}
                  />
                  <Typography variant="h5" fontWeight="bold">
                    Your Projects ({projectData?.length})
                  </Typography>
                </Box>

                {projectData.length === 0 ? (
                  <Paper
                    sx={{
                      textAlign: "center",
                      py: 8,
                      borderRadius: 4,
                      background: "rgba(255, 255, 255, 0.7)",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <ProjectIcon
                      sx={{ fontSize: 64, color: "#e0e0e0", mb: 2 }}
                    />
                    <Typography variant="h6" color="text.secondary" mb={1}>
                      No projects yet
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Create your first project to get started!
                    </Typography>
                  </Paper>
                ) : (
                  <Grid container spacing={3}>
                    {projectData.map((data, index) => (
                      <Grid item xs={12} sm={6} lg={4} key={index}>
                        <Zoom
                          in
                          timeout={800}
                          style={{ transitionDelay: `${index * 150}ms` }}
                        >
                          <Link
                            to={{
                            //  pathname: "/projectDetail/",
                               pathname: "/folder/projectDetail/",
                              search: `?id=${data.project_id}`,
                              state: { data },
                            }}
                            style={{ textDecoration: "none" }}
                          >
                            <Card
                              sx={{
                                height: "100%",
                                borderRadius: 4,
                                background: "rgba(255, 255, 255, 0.9)",
                                backdropFilter: "blur(10px)",
                                border: "1px solid rgba(255, 255, 255, 0.2)",
                                transition:
                                  "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                cursor: "pointer",
                                position: "relative",
                                overflow: "hidden",
                                "&:hover": {
                                  transform: "translateY(-8px) scale(1.02)",
                                  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
                                },
                                "&::before": {
                                  content: '""',
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: 4,
                                  background:
                                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                },
                              }}
                            >
                              <CardContent
                                sx={{
                                  p: 3,
                                  height: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <Box display="flex" alignItems="center" mb={2}>
                                  <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    fontSize={"15px"}
                                    sx={{
                                      whiteSpace: "normal",
                                      wordBreak: "break-word",
                                      overflowWrap: "break-word",
                                      textOverflow: "ellipsis",

                                      flex: 1,
                                    }}
                                  >
                                    Project Name: <br />
                                    {data.project_name}
                                  </Typography>
                                </Box>

                                <Box display="flex" alignItems="center" mb={3}>
                                  <Typography
                                    variant="body1"
                                    fontWeight="600"
                                    color="#4caf50"
                                  >
                                    Budget Cost: <span>₦ </span>
                                    {(Number(
                                      data.budget_cost) || 0
                                    ).toLocaleString("en-NG", {
                                      minimumFractionDigits: 2,
                                    })}
                                  </Typography>
                                </Box>

                                <Divider
                                  sx={{
                                    my: 2,
                                    background: "rgba(102, 126, 234, 0.1)",
                                  }}
                                />

                                <Box mt="auto">
                                  <Typography
                                    variant="subtitle2"
                                    mb={1.5}
                                    color="text.secondary"
                                  >
                                    Project Role Tags:
                                  </Typography>
                                  <Box display="flex" flexWrap="wrap" gap={1}>
                                    {typeof data.tags === "string" &&
                                      data.tags
                                        .split(",")
                                        .slice(0, 3)
                                        .map((tag, tagIndex) => (
                                          <Chip
                                            key={tagIndex}
                                            label={tag.trim()}
                                            size="small"
                                            sx={{
                                              background:
                                                "rgba(102, 126, 234, 0.1)",
                                              color: "#667eea",
                                              fontWeight: 500,
                                              border:
                                                "1px solid rgba(102, 126, 234, 0.2)",
                                            }}
                                          />
                                        ))}
                                    {typeof data.tags === "string" &&
                                      data.tags.split(",").length > 3 && (
                                        <Chip
                                          label={`+${
                                            data.tags.split(",").length - 3
                                          } more`}
                                          size="small"
                                          sx={{
                                            background:
                                              "rgba(118, 75, 162, 0.1)",
                                            color: "#764ba2",
                                            fontWeight: 500,
                                          }}
                                        />
                                      )}
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Link>
                        </Zoom>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Fade>
          </Container>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default ProjectSetup;
